<script setup lang="ts">
/**
 * 画布区：只提供一个宿主 div，真实 <svg> 由引擎挂载（Vue 绝不渲染其内部）。
 * 缩放/平移施加在引擎的 .sve-viewport 上，本组件只负责容器尺寸变化后通知重排。
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import BlankCanvasGuide from '@/components/BlankCanvasGuide.vue';
import PanelResizer from '@/components/PanelResizer.vue';
import TopBar from '@/components/TopBar.vue';
import { useEditor } from '@/composables/useEditor';
import { isEmbedded } from '@/embed';
import { isDrawTool, ui } from '@/stores/editor';

const editor = useEditor();
const host = ref<HTMLElement | null>(null);
let observer: ResizeObserver | null = null;

onMounted(() => {
  if (!host.value) return;
  editor.mount(host.value);
  // 尺寸变化（拖分栏 / 窗口缩放）→ 重排并保持居中
  observer = new ResizeObserver(() => editor.relayout());
  observer.observe(host.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
  editor.unmount();
});

// 文档变更 / 选中变更后重画选中框（版本号是唯一通知源）
watch([() => editor.version.value, () => editor.selection.value], () => editor.syncOverlay());

const zoomPercent = computed(() => Math.round(ui.zoom * 100));
const drawing = computed(() => isDrawTool(ui.tool));

/** 空画板引导卡（+ 画板虚线落区）：宿主嵌入时不打扰；关掉后画板再次变空会重新武装 */
const guide = computed(() => editor.blank.value && !ui.blankDismissed && !isEmbedded());
watch(editor.blank, (has: boolean) => {
  if (!has) ui.blankDismissed = false;
});
</script>

<template>
  <section class="canvas-wrap" aria-label="画布">
    <TopBar />

    <div
      ref="host"
      class="canvas-host"
      :class="{
        'is-hand': ui.tool === 'hand',
        'is-draw': drawing,
        'is-blank': guide,
        'is-dragover': guide && ui.dragOver,
      }"
      :data-tool="ui.tool"
    />

    <BlankCanvasGuide v-if="guide" />

    <PanelResizer side="list" />
    <PanelResizer side="right" />

    <div class="zoom-bar">
      <button title="缩小 Ctrl+-" @click="editor.setZoom(ui.zoom / 1.25)">−</button>
      <span class="val">{{ zoomPercent }}%</span>
      <button title="放大 Ctrl+=" @click="editor.setZoom(ui.zoom * 1.25)">+</button>
      <button title="适应窗口 Ctrl+1" @click="editor.zoomFit()">Fit</button>
    </div>
  </section>
</template>
